<template>
  <b-field label="Answer" :class="$style.container">
    <div>
      <div class="columns">
        <div class="column">
          <ValidationProvider
            v-slot="{ errors }"
            rules="required|max:50"
            name="This"
          >
            <b-field :class="$style.answerInputContainer">
              <div :class="$style.inputContainer">
                <div :class="$style.dotContainer">
                  <div :class="$style.dot" />
                </div>
                <b-input v-model="truthy" />
              </div>
              <p :class="$style.suggestionText">Positive Input</p>
              <p v-if="isSamePositiveInputError" :class="$style.errorText">
                Value cannot be same as negative input
              </p>
              <p v-if="errors.length" :class="$style.errorText">
                {{ errors[0] }}
              </p>
            </b-field>
          </ValidationProvider>
        </div>
      </div>
      <div class="columns">
        <div class="column">
          <ValidationProvider
            v-slot="{ errors }"
            rules="required|max:50"
            name="This"
          >
            <b-field :class="$style.answerInputContainer">
              <div :class="$style.inputContainer">
                <div :class="$style.dotContainer">
                  <div :class="$style.dot" />
                </div>
                <b-input v-model="falsy" />
              </div>
              <p :class="$style.suggestionText">Negative Input</p>
              <p v-if="isSameNegativeInputError" :class="$style.errorText">
                Value cannot be same as positive input
              </p>
              <p v-if="errors.length" :class="$style.errorText">
                {{ errors[0] }}
              </p>
            </b-field>
          </ValidationProvider>
        </div>
      </div>
      <div class="columns">
        <div class="column">
          <ValidationProvider v-slot="{ errors }" name="Not Applicable Value">
            <b-field
              :type="errors.length ? 'is-danger' : undefined"
              :message="errors[0]"
            >
              <div :class="$style.inputContainer">
                <div :class="$style.dotContainer">
                  <div :class="$style.dot" />
                </div>
                <b-input v-model="notApplicable" disabled />
              </div>
            </b-field>
          </ValidationProvider>
        </div>
      </div>
    </div>
  </b-field>
</template>

<script lang="ts">
import { Component, Prop, Watch, Vue } from 'vue-property-decorator';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import { BooleanCriterionContent } from '@/store/types/criterions.types';
import { isTruthy } from '@/jbi-shared/util/watcher.vue-decorator';

@Component({
  components: { ValidationProvider, ValidationObserver },
})
export default class BooleanCriterionInput extends Vue {
  @Prop({
    default() {
      return ['Yes', 'No', 'N/A'];
    },
  })
  public options!: BooleanCriterionContent['booleanOptions'];

  public truthy!: string;
  public falsy!: string;
  public notApplicable!: string;
  public isSamePositiveInputError!: boolean;
  public isSameNegativeInputError!: boolean;

  /** Props exist when `data()` runs; unlike `public truthy = this.options[0]`. */
  public data(): {
    truthy: string;
    falsy: string;
    notApplicable: string;
    isSamePositiveInputError: boolean;
    isSameNegativeInputError: boolean;
  } {
    const opts = this.options ?? ['Yes', 'No', 'N/A'];
    return {
      truthy: opts[0],
      falsy: opts[1],
      notApplicable: opts[2],
      isSamePositiveInputError: false,
      isSameNegativeInputError: false,
    };
  }

  private syncFromOptions(): void {
    const opts = this.options ?? ['Yes', 'No', 'N/A'];
    this.truthy = opts[0];
    this.falsy = opts[1];
    this.notApplicable = opts[2];
  }

  @Watch('truthy')
  public onChangedTruthy(newValue: string) {
    switch (newValue) {
      case '':
        if (this.isSameNegativeInputError) {
          this.isSameNegativeInputError = false;
        }
        this.isSamePositiveInputError = false;
        this.$emit('disableSubmitButton', true);
        break;
      case this.falsy:
        this.isSamePositiveInputError = true;
        this.$emit('disableSubmitButton', true);
        break;
      default:
        if (this.isSameNegativeInputError) {
          this.isSameNegativeInputError = false;
        }
        this.isSamePositiveInputError = false;
        this.$emit('disableSubmitButton', false);
        this.$emit('update:options', [
          this.truthy,
          this.falsy,
          this.notApplicable,
        ]);
    }
  }

  @Watch('falsy')
  public onChangedFalsy(newValue: string) {
    switch (newValue) {
      case '':
        if (this.isSamePositiveInputError) {
          this.isSamePositiveInputError = false;
        }
        this.isSameNegativeInputError = false;
        this.$emit('disableSubmitButton', true);
        break;
      case this.truthy:
        this.isSameNegativeInputError = true;
        this.$emit('disableSubmitButton', true);
        break;
      default:
        if (this.isSamePositiveInputError) {
          this.isSamePositiveInputError = false;
        }
        this.isSameNegativeInputError = false;
        this.$emit('disableSubmitButton', false);
        this.$emit('update:options', [
          this.truthy,
          this.falsy,
          this.notApplicable,
        ]);
    }
  }

  @isTruthy
  @Watch('options')
  public onRangeUpdated() {
    this.syncFromOptions();
  }
}
</script>

<style lang="scss" module>
.container {
  &#{&} {
    margin-bottom: 0;
  }

  :global .field {
    margin-bottom: 6px;
  }
}
.answerInputContainer {
  display: flex;
  flex-direction: column;
}
.inputContainer {
  display: flex;
  align-items: center;

  .dotContainer {
    margin-right: 8px;

    .dot {
      width: 12px;
      height: 12px;
      border-radius: 100%;
      border: 1px solid $grey-border-2;
    }
  }

  :global .control {
    flex: 1 1 100%;
  }

  + :global(.help) {
    margin-left: 20px;
  }
}
.suggestionText {
  margin-top: 5px;
  margin-left: 19px;
  font-size: 12px;
  color: #71797c;
}

.errorText {
  margin-top: 5px;
  margin-left: 19px;
  font-size: 12px;
  color: #f14668;
}
</style>
